import { FIELD, OFFSIDE } from "./config.js";
import type { PassSnapshot, Player, TeamSide } from "./types.js";

export type { PassSnapshot } from "./types.js";

/** Linea de medio campo. */
export const HALFWAY_X = FIELD.WIDTH / 2;

function attackDir(side: TeamSide): number {
  return side === "home" ? 1 : -1;
}

/** ¿Esta el jugador en mitad rival? */
export function inOpponentHalf(playerX: number, attackingSide: TeamSide): boolean {
  return attackingSide === "home" ? playerX > HALFWAY_X : playerX < HALFWAY_X;
}

/** ¿Todos los atacantes permanecen en su propia mitad? */
export function allAttackersInOwnHalf(
  attackingSide: TeamSide,
  attackers: readonly Pick<Player, "pos">[],
): boolean {
  if (attackers.length === 0) return true;
  return attackers.every((a) => !inOpponentHalf(a.pos.x, attackingSide));
}

/**
 * El PENULTIMO rival: el jugador que traza la linea.
 *
 * `opponents` tiene que llevar al PORTERO dentro. La regla habla del penultimo
 * *adversario*, y el portero es un adversario — normalmente el ultimo, asi que
 * el penultimo acaba siendo el ultimo defensa de campo, que es justo el jugador
 * del que todo el mundo espera que salga la linea.
 *
 * Aqui estaba el fallo que se veia en la repeticion: se pasaba la lista SIN
 * portero y aun asi se cogia el segundo, o sea el ANTEPENULTIMO adversario. La
 * linea salia por delante del ultimo defensa —a veces justo encima del balon,
 * que es como se noto— y ademas pitaba fueras de juego que no lo eran.
 *
 * Meter al portero en la lista, en vez de coger `sorted[0]` de los de campo,
 * tiene premio en el caso raro: con el portero adelantado el ultimo hombre ya
 * no es el, y la linea se recalcula sola como manda la regla.
 *
 * Devuelve `null` sin rivales: ahi la linea cae al medio campo.
 */
export function secondLastDefender(
  attackingSide: TeamSide,
  opponents: readonly Pick<Player, "pos">[],
): { x: number; y: number } | null {
  const dir = attackDir(attackingSide);
  const sorted = [...opponents].sort((a, b) =>
    dir > 0 ? b.pos.x - a.pos.x : a.pos.x - b.pos.x,
  );
  const pick = sorted.length >= 2 ? sorted[1] : sorted[0];
  return pick ? { x: pick.pos.x, y: pick.pos.y } : null;
}

/**
 * Coordenada X de la linea de fuera de juego estatica: mas restrictiva entre
 * balon y penultimo rival.
 */
export function offsideLineX(
  attackingSide: TeamSide,
  ballX: number,
  defenders: readonly Pick<Player, "pos">[],
): number {
  const dir = attackDir(attackingSide);
  const secondLastX = secondLastDefender(attackingSide, defenders)?.x ?? HALFWAY_X;

  if (dir > 0) return Math.max(ballX, secondLastX);
  return Math.min(ballX, secondLastX);
}

/**
 * Linea dinamica por frame: si ningun atacante ha penetrado en mitad rival,
 * la linea se fija en el centro del campo.
 */
export function dynamicOffsideLineX(
  attackingSide: TeamSide,
  ballX: number,
  defenders: readonly Pick<Player, "pos">[],
  attackers: readonly Pick<Player, "pos">[],
): number {
  if (attackers.length > 0 && allAttackersInOwnHalf(attackingSide, attackers)) {
    return HALFWAY_X;
  }
  return offsideLineX(attackingSide, ballX, defenders);
}

/**
 * Puntuacion de espacio respecto al fuera de juego (0 = ilegal, 1 = habilitado).
 * Cae a 0 en posicion ilegal; gradiente suave cerca de la linea.
 */
export function offsideSpaceScore(
  playerX: number,
  attackingSide: TeamSide,
  ballX: number,
  defenders: readonly Pick<Player, "pos">[],
  attackers: readonly Pick<Player, "pos">[],
): number {
  if (!inOpponentHalf(playerX, attackingSide)) return 1;

  const line = dynamicOffsideLineX(attackingSide, ballX, defenders, attackers);
  const dir = attackDir(attackingSide);
  const band = OFFSIDE.TOLERANCE;

  if (dir > 0) {
    if (playerX > line + band) return 0;
    if (playerX > line - band) return (line + band - playerX) / (2 * band);
    return 1;
  }

  if (playerX < line - band) return 0;
  if (playerX < line + band) return (playerX - (line - band)) / (2 * band);
  return 1;
}

/** ¿Posicion ilegal en el instante del pase? */
export function isInOffsidePosition(
  playerX: number,
  attackingSide: TeamSide,
  ballX: number,
  defenders: readonly Pick<Player, "pos">[],
  tolerance = 0,
  attackers: readonly Pick<Player, "pos">[] = [],
): boolean {
  if (!inOpponentHalf(playerX, attackingSide)) return false;

  const line = dynamicOffsideLineX(attackingSide, ballX, defenders, attackers);
  const dir = attackDir(attackingSide);

  if (dir > 0) return playerX > line + tolerance;
  return playerX < line - tolerance;
}

/**
 * La infraccion, con TODO lo que hace falta para dibujarla.
 *
 * Devuelve tambien la linea y el defensor que la traza, no solo el punto: es lo
 * que permite que la revision de VAR enseñe la misma geometria con la que se ha
 * decidido, en vez de recalcularla por su cuenta y arriesgarse a contar otra
 * historia.
 */
export interface OffsideViolation {
  spot: { x: number; y: number };
  /** X de la linea en el instante del pase. */
  lineX: number;
  /** Posicion del penultimo rival en ese instante. */
  defender: { x: number; y: number };
  /**
   * Quien manda en la linea.
   *
   * La regla mira DOS cosas —el balon y el penultimo rival— y se queda con la
   * mas restrictiva. Casi siempre es el defensa, pero en un pase hacia atras el
   * que corta es el balon. Sin esta distincion la repeticion trazaba la linea
   * sobre un jugador que en esa jugada no pintaba nada.
   */
  lineFrom: "defender" | "ball";
  /** Donde estaba el balon al jugarse. */
  ball: { x: number; y: number };
}

/** Comprueba si un receptor estaba fuera de juego cuando se jugo el balon. */
export function checkOffsideOnReceive(
  receiver: Player,
  snapshot: PassSnapshot,
  allPlayers: readonly Player[],
  tolerance = OFFSIDE.TOLERANCE,
): OffsideViolation | null {
  if (receiver.side !== snapshot.attackingSide) return null;
  if (receiver.id === snapshot.passerId) return null;

  const pos = snapshot.positions.get(receiver.id);
  if (!pos) return null;

  // CON portero: la regla habla del penultimo adversario, y el portero lo es.
  const defenders = allPlayers.filter((p) => p.side !== snapshot.attackingSide);
  const attackers = allPlayers.filter(
    (p) => p.side === snapshot.attackingSide && p.role !== "gk",
  );
  const defenderPositions = defenders.map((d) => {
    const snap = snapshot.positions.get(d.id);
    return { pos: snap ?? d.pos };
  });
  const attackerPositions = attackers.map((a) => {
    const snap = snapshot.positions.get(a.id);
    return { pos: snap ?? a.pos };
  });

  if (
    !isInOffsidePosition(
      pos.x,
      snapshot.attackingSide,
      snapshot.ballX,
      defenderPositions,
      tolerance,
      attackerPositions,
    )
  ) {
    return null;
  }

  const line = dynamicOffsideLineX(
    snapshot.attackingSide,
    snapshot.ballX,
    defenderPositions,
    attackerPositions,
  );
  const last = secondLastDefender(snapshot.attackingSide, defenderPositions);
  // Sin rivales la linea es el medio campo: la marca se pone sobre ella misma,
  // para que el dibujo no invente un jugador que no existe.
  const defender = last ?? { x: line, y: pos.y };

  return {
    spot: { x: pos.x, y: pos.y },
    lineX: line,
    defender,
    lineFrom: Math.abs(line - defender.x) < 1e-6 ? "defender" : "ball",
    ball: { x: snapshot.ballX, y: snapshot.ballY },
  };
}

/**
 * Donde se saca la falta, de verdad.
 *
 * El fuera de juego se pita DONDE ESTABA EL ATACANTE, y eso casi siempre cae
 * dentro del area del que defiende. Sacar desde ahi era la peor version posible
 * de la regla: castigabas al atacante y le devolvias el balon en dos segundos,
 * con un despeje corto a puerta vacia. El reglamento real ya resuelve esto —un
 * indirecto dentro del area pequeña se saca desde la linea del area— y aqui se
 * lleva hasta el borde del area grande, que a esta escala es la distancia con
 * la que el saque deja de ser una trampa.
 *
 * `defendingSide` es quien SACA (el equipo al que le hicieron el fuera de juego
 * en contra), asi que su porteria esta en el extremo del que hay que alejarse.
 */
export function offsideRestartSpot(
  defendingSide: TeamSide,
  spot: { x: number; y: number },
): { x: number; y: number } {
  const margin = OFFSIDE.SPOT_MARGIN;
  const y = Math.max(margin, Math.min(FIELD.HEIGHT - margin, spot.y));
  const clearance = FIELD.BOX_WIDTH + OFFSIDE.SPOT_BOX_CLEARANCE;
  const x =
    defendingSide === "home"
      ? Math.max(clearance, Math.min(FIELD.WIDTH - margin, spot.x))
      : Math.min(FIELD.WIDTH - clearance, Math.max(margin, spot.x));
  return { x, y };
}

/**
 * Donde acaba un jugador al que le toca retirarse a la barrera.
 *
 * Se empuja EN LINEA RECTA desde el balon, sin recolocar a nadie: el bloque
 * conserva la forma que tenia y solo se abre el hueco del saque. `awayDir` es
 * hacia donde retrocede quien esta justo encima del balon, donde no hay
 * direccion que respetar — su propia porteria.
 */
export function wallRetreatPos(
  player: { x: number; y: number },
  at: { x: number; y: number },
  distance: number,
  awayDir: number,
): { x: number; y: number } {
  const dx = player.x - at.x;
  const dy = player.y - at.y;
  // `sqrt` y no `hypot`: esto decide el saque en red, y `hypot` no da el mismo
  // bit en todos los navegadores. Ver `apps/game/src/core/math.ts`.
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d >= distance) return { x: player.x, y: player.y };
  const nx = d > 1e-3 ? dx / d : awayDir;
  const ny = d > 1e-3 ? dy / d : 0;
  return { x: at.x + nx * distance, y: at.y + ny * distance };
}

/** Registra posiciones en el instante de un pase o centro. */
export function createPassSnapshot(
  passer: Player,
  ballX: number,
  ballY: number,
  kind: "pass" | "cross",
  allPlayers: readonly Player[],
): PassSnapshot {
  const positions = new Map<string, { x: number; y: number }>();
  for (const p of allPlayers) {
    positions.set(p.id, { x: p.pos.x, y: p.pos.y });
  }
  return {
    passerId: passer.id,
    attackingSide: passer.side,
    ballX,
    ballY,
    positions,
    kind,
  };
}
